/**
 * Focused Review Mode
 * 
 * Full-screen distraction-free interface for reviewing transactions
 * during onboarding or regular workflow
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Edit2, SkipForward, X, Sparkles, Info, Loader2 } from 'lucide-react';
import { classifyWithClaude } from '@/services/claude-classification';
import { useAuth } from '@/contexts/auth-context';

interface Transaction {
  id: string;
  date: string;
  counterparty: string;
  description: string;
  amount: number;
  debitAccount?: string;
  creditAccount?: string;
  vatRate?: number;
}

interface AISuggestion {
  debitAccount: string;
  debitAccountName: string;
  creditAccount: string;
  creditAccountName: string;
  vatRate: number;
  confidence: number;
  reasoning: string;
}

interface FocusedReviewModeProps {
  transactions: Transaction[];
  isOnboarding?: boolean;
  hasHistoricalData?: boolean;
  onComplete: (results: ReviewResult[]) => void;
  onExit?: () => void;
}

interface ReviewResult {
  transactionId: string;
  action: 'approved' | 'edited' | 'skipped';
  debitAccount: string;
  creditAccount: string;
  vatRate: number;
  createRule: boolean;
}

export const FocusedReviewMode: React.FC<FocusedReviewModeProps> = ({
  transactions,
  isOnboarding = false,
  hasHistoricalData = false,
  onComplete,
  onExit,
}) => {
  const { organization } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<ReviewResult[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editedAccount, setEditedAccount] = useState('');
  const [editedVAT, setEditedVAT] = useState('8.1');
  const [createRule, setCreateRule] = useState(true);
  const [showTutorial, setShowTutorial] = useState(isOnboarding && currentIndex < 3);
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestion | null>(null);
  const [isClassifying, setIsClassifying] = useState(false);

  const currentTransaction = transactions[currentIndex];
  const progress = ((currentIndex + 1) / transactions.length) * 100;

  // Fetch AI classification for current transaction
  useEffect(() => {
    if (!currentTransaction) return;
    
    const fetchClassification = async () => {
      setIsClassifying(true);
      try {
          const result = await classifyWithClaude(
          {
            description: currentTransaction.description || currentTransaction.counterparty,
            amount: currentTransaction.amount,
            currency: 'CHF',
            date: currentTransaction.date,
            counterparty: currentTransaction.counterparty,
            source: 'manual',
            originalRaw: { description: currentTransaction.description || '' },
          },
          undefined, // mandateId - will be set after onboarding
          organization?.id
        );

        setAiSuggestion({
          debitAccount: result.accountNumber,
          debitAccountName: result.accountName,
          creditAccount: '1020', // Default bank account
          creditAccountName: 'Bank PostFinance',
          vatRate: result.vatRate || 0,
          confidence: result.confidence,
          reasoning: result.reasoning || 'AI classification based on transaction details',
        });
      } catch (error) {
        console.error('Classification error:', error);
        // Fallback to basic suggestion
        setAiSuggestion({
          debitAccount: '6000',
          debitAccountName: 'General Expense',
          creditAccount: '1020',
          creditAccountName: 'Bank PostFinance',
          vatRate: 8.1,
          confidence: 50,
          reasoning: 'Fallback classification - please review carefully',
        });
      } finally {
        setIsClassifying(false);
      }
    };

    fetchClassification();
  }, [currentIndex, currentTransaction, organization]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'enter':
          handleApprove();
          break;
        case 'e':
          setEditMode(true);
          break;
        case 's':
          handleSkip();
          break;
        case 'escape':
          if (editMode) {
            setEditMode(false);
          } else if (onExit) {
            handleExit();
          }
          break;
        case 'arrowleft':
          if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
          }
          break;
        case 'arrowright':
          if (currentIndex < transactions.length - 1) {
            setCurrentIndex(prev => prev + 1);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, editMode]);

  // Update tutorial visibility
  useEffect(() => {
    if (isOnboarding) {
      setShowTutorial(currentIndex < 3);
    }
  }, [currentIndex, isOnboarding]);

  const handleApprove = () => {
    if (!aiSuggestion) return;
    
    const result: ReviewResult = {
      transactionId: currentTransaction.id,
      action: 'approved',
      debitAccount: aiSuggestion.debitAccount,
      creditAccount: aiSuggestion.creditAccount,
      vatRate: aiSuggestion.vatRate,
      createRule: false,
    };

    saveResultAndContinue(result);
  };

  const handleEdit = () => {
    if (!aiSuggestion) return;
    
    if (!editMode) {
      setEditMode(true);
      setEditedAccount(aiSuggestion.debitAccount);
      setEditedVAT(aiSuggestion.vatRate.toString());
      return;
    }

    const result: ReviewResult = {
      transactionId: currentTransaction.id,
      action: 'edited',
      debitAccount: editedAccount,
      creditAccount: aiSuggestion.creditAccount,
      vatRate: parseFloat(editedVAT),
      createRule,
    };

    saveResultAndContinue(result);
    setEditMode(false);
  };

  const handleSkip = () => {
    if (!aiSuggestion) return;
    
    const result: ReviewResult = {
      transactionId: currentTransaction.id,
      action: 'skipped',
      debitAccount: aiSuggestion.debitAccount,
      creditAccount: aiSuggestion.creditAccount,
      vatRate: aiSuggestion.vatRate,
      createRule: false,
    };

    saveResultAndContinue(result);
  };

  const saveResultAndContinue = (result: ReviewResult) => {
    const newResults = [...results, result];
    setResults(newResults);

    if (currentIndex < transactions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Review complete
      onComplete(newResults);
    }
  };

  const handleExit = () => {
    if (confirm('Exit review? Your progress will be saved.')) {
      onComplete(results);
      onExit?.();
    }
  };

  if (!currentTransaction) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-brand-white z-50 overflow-y-auto">
      {/* Header */}
      <div className="border-b border-brand-border bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              {isOnboarding && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Onboarding Tutorial
                </Badge>
              )}
            </div>

            {/* Progress */}
            <div className="flex-1 max-w-md mx-8">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-brand-primary">
                  Transaction {currentIndex + 1} of {transactions.length}
                </span>
                <span className="text-brand-text-secondary">{progress.toFixed(0)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Exit */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExit}
              className="text-brand-text-secondary hover:text-brand-primary"
            >
              <X className="h-4 w-4 mr-1" />
              Save & Exit (ESC)
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Transaction Details */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-brand-text-secondary mb-4">
                Transaction Details
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <Label className="text-xs text-brand-text-secondary">Date</Label>
                  <p className="font-medium">{currentTransaction.date}</p>
                </div>
                <div>
                  <Label className="text-xs text-brand-text-secondary">Counterparty</Label>
                  <p className="font-medium">{currentTransaction.counterparty}</p>
                </div>
                <div>
                  <Label className="text-xs text-brand-text-secondary">Description</Label>
                  <p className="text-brand-text-secondary">{currentTransaction.description}</p>
                </div>
                <div>
                  <Label className="text-xs text-brand-text-secondary">Amount</Label>
                  <p className={`text-lg font-bold ${currentTransaction.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    CHF {Math.abs(currentTransaction.amount).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Center: AI Suggestion */}
          <Card className="border-2 border-brand-primary">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-brand-primary flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  AI Suggestion
                </h3>
                {aiSuggestion && (
                  <Badge
                    variant="outline"
                    className={
                      aiSuggestion.confidence >= 95
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : aiSuggestion.confidence >= 85
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-orange-50 text-orange-700 border-orange-200'
                    }
                  >
                    {aiSuggestion.confidence}% confident
                  </Badge>
                )}
              </div>

              {isClassifying || !aiSuggestion ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-primary mb-3" />
                  <p className="text-sm text-brand-text-secondary">AI is analyzing transaction...</p>
                </div>
              ) : !editMode ? (
                <div className="space-y-4">
                  <div className="bg-brand-secondary rounded-lg p-4">
                    <div className="space-y-2 text-sm">
                      <div>
                        <Label className="text-xs text-brand-text-secondary">Debit Account</Label>
                        <p className="font-medium">
                          {aiSuggestion.debitAccount} - {aiSuggestion.debitAccountName}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-brand-text-secondary">Credit Account</Label>
                        <p className="font-medium">
                          {aiSuggestion.creditAccount} - {aiSuggestion.creditAccountName}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-brand-text-secondary">VAT Rate</Label>
                        <p className="font-medium">{aiSuggestion.vatRate}%</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800 flex items-start">
                      <Info className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                      {aiSuggestion.reasoning}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="editAccount">Debit Account</Label>
                    <Input
                      id="editAccount"
                      value={editedAccount}
                      onChange={(e) => setEditedAccount(e.target.value)}
                      placeholder="e.g., 6720"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Label htmlFor="editVAT">VAT Rate</Label>
                    <Select value={editedVAT} onValueChange={setEditedVAT}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% (Exempt)</SelectItem>
                        <SelectItem value="2.5">2.5% (Reduced)</SelectItem>
                        <SelectItem value="3.7">3.7% (Accommodation)</SelectItem>
                        <SelectItem value="8.1">8.1% (Standard)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="createRule"
                      checked={createRule}
                      onChange={(e) => setCreateRule(e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="createRule" className="text-sm cursor-pointer">
                      Create rule from this correction
                    </Label>
                  </div>
                  <p className="text-xs text-brand-text-secondary">
                    Future transactions from "{currentTransaction.counterparty}" will use this classification
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Actions */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-brand-text-secondary mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Button
                  onClick={handleApprove}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  size="lg"
                  disabled={editMode || isClassifying || !aiSuggestion}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Looks Good
                  <kbd className="ml-auto text-xs bg-green-700 px-2 py-0.5 rounded">Enter</kbd>
                </Button>

                <Button
                  onClick={handleEdit}
                  variant={editMode ? 'default' : 'outline'}
                  className="w-full"
                  size="lg"
                  disabled={isClassifying || !aiSuggestion}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  {editMode ? 'Save Changes' : 'Edit Account'}
                  {!editMode && <kbd className="ml-auto text-xs bg-brand-secondary px-2 py-0.5 rounded">E</kbd>}
                </Button>

                <Button
                  onClick={handleSkip}
                  variant="outline"
                  className="w-full"
                  size="lg"
                  disabled={editMode || isClassifying || !aiSuggestion}
                >
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip for Now
                  <kbd className="ml-auto text-xs bg-brand-secondary px-2 py-0.5 rounded">S</kbd>
                </Button>
              </div>

              {showTutorial && (
                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-yellow-900 mb-2">Tutorial Tips</h4>
                  <ul className="text-xs text-yellow-800 space-y-1">
                    {currentIndex === 0 && (
                      <>
                        <li>• Review the AI suggestion carefully</li>
                        <li>• Use keyboard shortcuts for speed</li>
                        <li>• Approve if correct, Edit if wrong</li>
                      </>
                    )}
                    {currentIndex === 1 && (
                      <>
                        <li>• Press "E" to edit account</li>
                        <li>• Create rules to train the AI</li>
                        <li>• Higher confidence = better AI</li>
                      </>
                    )}
                    {currentIndex === 2 && (
                      <>
                        <li>• Navigate with ← → arrow keys</li>
                        <li>• Press ESC to exit anytime</li>
                        <li>• Your progress is auto-saved</li>
                      </>
                    )}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
          >
            ← Previous
          </Button>
          <span className="text-sm text-brand-text-secondary">
            Transaction {currentIndex + 1} / {transactions.length}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(prev => Math.min(transactions.length - 1, prev + 1))}
            disabled={currentIndex === transactions.length - 1}
          >
            Next →
          </Button>
        </div>
      </div>
    </div>
  );
};
